"use client";

import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PlayerAvatar } from "@/components/shared/player-avatar";
import { Shield, Users, Target, Medal } from "lucide-react";
import { motion } from "framer-motion";
import type { VysledokHraca } from "@/types/database";
import {
  getHracId,
  getHracMeno,
  getPozicia,
  getKlub,
  getPocetHlasov,
} from "@/types/database";
import { cn, POZICIA_LABELS } from "@/lib/utils";

interface SlotsViewProps {
  vysledky: VysledokHraca[];
}

const itemAnimation = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 },
};

const TABS_TRIGGER_CLASSES =
  "data-[state=active]:bg-linear-to-r data-[state=active]:from-brand-gradient-from data-[state=active]:to-brand-gradient-to data-[state=active]:text-primary-foreground";

export function SlotsView({ vysledky }: SlotsViewProps) {
  const [activeTab, setActiveTab] = useState("brankar");

  // Zoskupenie agregovaných výsledkov podľa pozícií
  const zoskupeneVysledky = useMemo(() => {
    const grouped: Record<string, VysledokHraca[]> = {
      brankar: [],
      obranca: [],
      utocnik: [],
    };

    vysledky.forEach((vysledok) => {
      if (grouped[getPozicia(vysledok)]) {
        grouped[getPozicia(vysledok)].push(vysledok);
      }
    });

    // Zoradenie podľa počtu hlasov
    Object.keys(grouped).forEach((pozicia) => {
      grouped[pozicia].sort((a, b) => getPocetHlasov(b) - getPocetHlasov(a));
    });

    return grouped;
  }, [vysledky]);

  const maxHlasyValue = useMemo(() => {
    let max = 0;
    vysledky.forEach((hrac) => {
      if (getPocetHlasov(hrac) > max) max = getPocetHlasov(hrac);
    });
    return max;
  }, [vysledky]);

  const availablePozicie = useMemo(
    () =>
      Object.keys(zoskupeneVysledky).filter(
        (p) => zoskupeneVysledky[p].length > 0,
      ),
    [zoskupeneVysledky],
  );

  const getPoziciaIcon = (pozicia: string) => {
    switch (pozicia) {
      case "brankar":
        return <Shield className="h-5 w-5 text-status-info" />;
      case "obranca":
        return <Users className="h-5 w-5 text-primary" />;
      case "utocnik":
        return <Target className="h-5 w-5 text-status-success" />;
      default:
        return null;
    }
  };

  const getPoziciaColor = (pozicia: string) => {
    switch (pozicia) {
      case "brankar":
        return "from-status-info to-status-info/80";
      case "obranca":
        return "from-brand-gradient-from to-brand-gradient-to";
      case "utocnik":
        return "from-status-success to-status-success/80";
      default:
        return "from-muted to-muted-foreground";
    }
  };

  if (availablePozicie.length === 0) {
    return (
      <Card>
        <CardContent className="text-center py-12">
          <p className="text-muted-foreground">
            Žiadne výsledky pre zobrazenie.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-8">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-1 md:grid-cols-3 bg-muted/50 p-1 h-auto">
          {availablePozicie.map((p) => (
            <TabsTrigger key={p} value={p} className={TABS_TRIGGER_CLASSES}>
              <div className="flex items-center gap-2">
                {getPoziciaIcon(p)}
                <span className="font-semibold">
                  {POZICIA_LABELS[p as keyof typeof POZICIA_LABELS]}
                </span>
              </div>
            </TabsTrigger>
          ))}
        </TabsList>

        {availablePozicie.map((pozicia) => {
          const hraci = zoskupeneVysledky[pozicia] || [];
          return (
            <TabsContent key={pozicia} value={pozicia} className="mt-6">
              <Card className="border-muted/60 shadow-sm">
                <CardHeader
                  className={cn(
                    "bg-linear-to-r py-4 border-b border-muted/40",
                    getPoziciaColor(pozicia),
                  )}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {getPoziciaIcon(pozicia)}
                      <h3 className="text-lg font-bold text-primary-foreground">
                        {POZICIA_LABELS[pozicia as keyof typeof POZICIA_LABELS]}
                      </h3>
                    </div>
                    <span className="text-sm text-primary-foreground/80 font-medium">
                      {hraci.length} {hraci.length === 1 ? "hráč" : "hráčov"}
                    </span>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  {hraci.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      Žiadni hráči
                    </div>
                  ) : (
                    <div className="divide-y divide-muted/30">
                      {hraci.map((hrac, index) => {
                        const percentage =
                          maxHlasyValue > 0
                            ? (getPocetHlasov(hrac) / maxHlasyValue) * 100
                            : 0;

                        return (
                          <motion.div
                            key={getHracId(hrac)}
                            variants={itemAnimation}
                            initial="hidden"
                            animate="show"
                            transition={{ delay: index * 0.05 }}
                            className="p-4 hover:bg-muted/20 transition-colors"
                          >
                            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                              <div className="flex items-center gap-4 flex-1">
                                <div className="flex items-center gap-3">
                                  <span className="text-lg font-bold text-muted-foreground w-6">
                                    {index + 1}.
                                  </span>
                                  {index < 3 && (
                                    <Medal
                                      className={cn(
                                        "h-5 w-5",
                                        index === 0 && "text-hockey-gold",
                                        index === 1 && "text-muted-foreground",
                                        index === 2 && "text-amber-700",
                                      )}
                                    />
                                  )}
                                </div>
                                <PlayerAvatar
                                  hracId={getHracId(hrac)}
                                  meno={getHracMeno(hrac)}
                                  pozicia={getPozicia(hrac)}
                                  className="h-12 w-12 border-2 border-muted"
                                />
                                <div className="flex flex-col flex-1 min-w-0">
                                  <span className="font-semibold text-base truncate">
                                    {getHracMeno(hrac)}
                                  </span>
                                  <span className="text-sm text-muted-foreground truncate">
                                    {getKlub(hrac) || "Bez klubu"}
                                  </span>
                                </div>
                              </div>
                              <div className="flex items-center gap-4 sm:w-64">
                                <div className="flex-1">
                                  <Progress
                                    value={percentage}
                                    className="h-2"
                                  />
                                </div>
                                <div className="flex items-center gap-2 min-w-[80px] justify-end">
                                  <span className="text-2xl font-bold text-primary">
                                    {getPocetHlasov(hrac)}
                                  </span>
                                  <span className="text-xs text-muted-foreground uppercase">
                                    {getPocetHlasov(hrac) === 1
                                      ? "hlas"
                                      : "hlasov"}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </motion.div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          );
        })}
      </Tabs>
    </div>
  );
}
